/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  NetworkContext,
  VPCContext,
  SubnetContext,
  RouteTableContext,
  SecurityGroupContext,
  SecurityRuleContext,
  NaclRuleContext,
} from './types';

/**
 * Transforms discovery.json into a structured context for the LLM.
 * This is critical - the LLM needs clean, well-organized data to analyze.
 */
export class ContextBuilder {
  /**
   * Build context from AWS discovery data
   */
  static fromAWSDiscovery(discovery: any, region: string, accountId: string): NetworkContext {
    const vpcs: VPCContext[] = [];

    // discovery.vpcs is an object keyed by vpc_id
    for (const [vpcId, vpcData] of Object.entries(discovery.vpcs || {})) {
      const vpc = vpcData as any;

      // Build subnet context
      const subnets: SubnetContext[] = (vpc.subnets || []).map((s: any) => ({
        id: s.id,
        name: s.tags?.Name || s.id,
        cidr: s.cidr_block,
        availabilityZone: s.availability_zone,
        isPublic: s.map_public_ip_on_launch || false,
        routeTableId: this.findRouteTableForSubnet(s.id, vpc.route_tables),
        networkAclId: this.findNaclForSubnet(s.id, vpc.network_acls),
      }));

      // Build route table context
      const routeTables: RouteTableContext[] = (vpc.route_tables || []).map((rt: any) => ({
        id: rt.id,
        name: rt.tags?.Name || rt.id,
        routes: (rt.routes || []).map((r: any) => ({
          destination: r.DestinationCidrBlock || r.DestinationPrefixListId || 'unknown',
          target:
            r.GatewayId ||
            r.NatGatewayId ||
            r.TransitGatewayId ||
            r.VpcPeeringConnectionId ||
            r.NetworkInterfaceId ||
            'local',
          targetType: this.inferTargetType(r),
        })),
        associatedSubnets: (rt.associations || [])
          .filter((a: any) => a.SubnetId)
          .map((a: any) => a.SubnetId),
      }));

      // Build security group context
      const securityGroups: SecurityGroupContext[] = (vpc.security_groups || []).map(
        (sg: any) => ({
          id: sg.id,
          name: sg.group_name || sg.tags?.Name || sg.id,
          description: sg.description || '',
          inboundRules: (sg.ip_permissions || []).flatMap((p: any) =>
            this.parseAWSSecurityRule(p)
          ),
          outboundRules: (sg.ip_permissions_egress || []).flatMap((p: any) =>
            this.parseAWSSecurityRule(p)
          ),
          attachedResources: this.findResourcesWithSG(sg.id, vpc),
        })
      );

      // Build network ACL context
      const networkAcls = (vpc.network_acls || []).map((nacl: any) => ({
        id: nacl.id,
        name: nacl.tags?.Name || nacl.id,
        isDefault: nacl.is_default || false,
        inboundRules: (nacl.entries || [])
          .filter((e: any) => !e.Egress)
          .map((e: any) => this.parseAWSNaclRule(e)),
        outboundRules: (nacl.entries || [])
          .filter((e: any) => e.Egress)
          .map((e: any) => this.parseAWSNaclRule(e)),
        associatedSubnets: (nacl.associations || []).map((a: any) => a.SubnetId),
      }));

      // Build gateway context
      const gateways = [
        ...(vpc.internet_gateways || []).map((g: any) => ({
          id: g.id,
          name: g.tags?.Name || g.id,
          type: 'igw' as const,
          attachedVpc: vpcId,
        })),
        ...(vpc.nat_gateways || []).map((g: any) => ({
          id: g.id,
          name: g.tags?.Name || g.id,
          type: 'nat' as const,
          subnetId: g.subnet_id,
          privateIp: g.nat_gateway_addresses?.[0]?.PrivateIp,
        })),
        ...(vpc.transit_gateways || []).map((g: any) => ({
          id: g.id,
          name: g.tags?.Name || g.id,
          type: 'tgw' as const,
        })),
      ];

      // Build instance context
      const instances = (vpc.instances || []).map((i: any) => ({
        id: i.id,
        name: i.tags?.Name || i.id,
        privateIp: i.private_ip_address,
        publicIp: i.public_ip_address,
        subnetId: i.subnet_id,
        securityGroups: i.security_groups || [],
      }));

      vpcs.push({
        id: vpcId,
        name: vpc.info?.tags?.Name || vpcId,
        cidr: vpc.info?.cidr_block,
        subnets,
        routeTables,
        securityGroups,
        networkAcls,
        gateways,
        instances,
      });
    }

    return {
      provider: 'aws',
      region,
      accountId,
      vpcs,
    };
  }

  /**
   * Build context from OCI discovery data
   */
  static fromOCIDiscovery(discovery: any, region: string, tenancyId: string): NetworkContext {
    const vpcs: VPCContext[] = [];

    // discovery.vcns is an object keyed by vcn_id
    for (const [vcnId, vcnData] of Object.entries(discovery.vcns || {})) {
      const vcn = vcnData as any;

      // Build subnet context
      const subnets: SubnetContext[] = (vcn.subnets || []).map((s: any) => ({
        id: s.id,
        name: s.display_name || s.id,
        cidr: s.cidr_block,
        availabilityZone: s.availability_zone,
        isPublic: !s.prohibit_public_ip_on_vnic,
        routeTableId: s.route_table_id,
        securityListIds: s.security_list_ids || [],
      }));

      // Build route table context
      const routeTables: RouteTableContext[] = (vcn.route_tables || []).map((rt: any) => ({
        id: rt.id,
        name: rt.display_name || rt.id,
        routes: (rt.route_rules || []).map((r: any) => ({
          destination: r.destination || r.destination_type,
          target: r.network_entity_id,
          targetType: this.inferOCITargetType(r.network_entity_id),
        })),
        associatedSubnets: subnets.filter((s: any) => s.routeTableId === rt.id).map((s) => s.id),
      }));

      // Build security group context (from Security Lists + NSGs)
      const securityGroups: SecurityGroupContext[] = [
        // Security Lists (stateless, like NACLs but used differently)
        ...(vcn.security_lists || []).map((sl: any) => ({
          id: sl.id,
          name: sl.display_name || sl.id,
          description: 'Security List',
          inboundRules: (sl.ingress_security_rules || []).map((r: any) =>
            this.parseOCISecurityRule(r, 'inbound')
          ),
          outboundRules: (sl.egress_security_rules || []).map((r: any) =>
            this.parseOCISecurityRule(r, 'outbound')
          ),
          attachedResources: subnets
            .filter((s: any) => s.securityListIds?.includes(sl.id))
            .map((s) => s.id),
        })),
        // Network Security Groups (stateful, like AWS SGs)
        ...(vcn.network_security_groups || []).map((nsg: any) => ({
          id: nsg.id,
          name: nsg.display_name || nsg.id,
          description: 'Network Security Group (stateful)',
          inboundRules: (nsg.security_rules || [])
            .filter((r: any) => r.direction === 'INGRESS')
            .map((r: any) => this.parseOCINSGRule(r)),
          outboundRules: (nsg.security_rules || [])
            .filter((r: any) => r.direction === 'EGRESS')
            .map((r: any) => this.parseOCINSGRule(r)),
          attachedResources: [],
        })),
      ];

      // Build gateway context
      const gateways = [
        ...(vcn.internet_gateways || []).map((g: any) => ({
          id: g.id,
          name: g.display_name || g.id,
          type: 'igw' as const,
          attachedVpc: vcnId,
        })),
        ...(vcn.nat_gateways || []).map((g: any) => ({
          id: g.id,
          name: g.display_name || g.id,
          type: 'nat' as const,
        })),
        ...(vcn.service_gateways || []).map((g: any) => ({
          id: g.id,
          name: g.display_name || g.id,
          type: 'sgw' as const,
        })),
        ...(vcn.drg_attachments || []).map((g: any) => ({
          id: g.drg_id,
          name: g.display_name || g.drg_id,
          type: 'drg' as const,
        })),
      ];

      // Build instance context
      const instances = (vcn.instances || []).map((i: any) => ({
        id: i.id,
        name: i.tags?.Name || i.id,
        privateIp: '',
        subnetId: '',
        securityGroups: [],
      }));

      vpcs.push({
        id: vcnId,
        name: vcn.info?.display_name || vcnId,
        cidr: vcn.info?.cidr_block,
        subnets,
        routeTables,
        securityGroups,
        networkAcls: [],
        gateways,
        instances,
      });
    }

    return {
      provider: 'oci',
      region,
      accountId: tenancyId,
      vpcs,
    };
  }

  // Helper methods

  private static findRouteTableForSubnet(subnetId: string, routeTables: any[]): string {
    for (const rt of routeTables || []) {
      for (const assoc of rt.associations || []) {
        if (assoc.SubnetId === subnetId) {
          return rt.id;
        }
      }
    }
    // Return main/default route table if no explicit association
    const mainRt = routeTables?.find((rt: any) =>
      rt.associations?.some((a: any) => a.Main === true)
    );
    return mainRt?.id || '';
  }

  private static findNaclForSubnet(subnetId: string, nacls: any[]): string {
    for (const nacl of nacls || []) {
      for (const assoc of nacl.associations || []) {
        if (assoc.SubnetId === subnetId) {
          return nacl.id;
        }
      }
    }
    return '';
  }

  private static inferTargetType(route: any): string {
    if (route.GatewayId?.startsWith('igw-')) return 'igw';
    if (route.NatGatewayId) return 'nat';
    if (route.TransitGatewayId) return 'tgw';
    if (route.VpcPeeringConnectionId) return 'pcx';
    if (route.GatewayId?.startsWith('vgw-')) return 'vgw';
    if (route.GatewayId === 'local') return 'local';
    return 'local';
  }

  private static inferOCITargetType(entityId: string): string {
    if (!entityId) return 'local';
    if (entityId.includes('internetgateway')) return 'igw';
    if (entityId.includes('natgateway')) return 'nat';
    if (entityId.includes('drg')) return 'drg';
    if (entityId.includes('servicegateway')) return 'sgw';
    if (entityId.includes('localpeeringgateway')) return 'lgw';
    return 'local';
  }

  private static parseAWSSecurityRule(permission: any): SecurityRuleContext[] {
    const rules: SecurityRuleContext[] = [];

    const protocol = permission.IpProtocol === '-1' ? 'all' : permission.IpProtocol;
    const portRange =
      permission.FromPort === permission.ToPort
        ? String(permission.FromPort || 'all')
        : `${permission.FromPort || 0}-${permission.ToPort || 65535}`;

    // CIDR ranges
    for (const range of permission.IpRanges || []) {
      rules.push({
        protocol,
        portRange,
        source: range.CidrIp,
        description: range.Description,
      });
    }

    // IPv6 ranges
    for (const range of permission.Ipv6Ranges || []) {
      rules.push({
        protocol,
        portRange,
        source: range.CidrIpv6,
        description: range.Description,
      });
    }

    // Security group references
    for (const sgRef of permission.UserIdGroupPairs || []) {
      rules.push({
        protocol,
        portRange,
        source: sgRef.GroupId,
        description: sgRef.Description,
      });
    }

    return rules;
  }

  private static parseAWSNaclRule(entry: any): NaclRuleContext {
    return {
      ruleNumber: entry.RuleNumber,
      protocol: entry.Protocol === '-1' ? 'all' : entry.Protocol,
      portRange: entry.PortRange ? `${entry.PortRange.From}-${entry.PortRange.To}` : 'all',
      cidr: entry.CidrBlock || entry.Ipv6CidrBlock,
      action: entry.RuleAction?.toLowerCase() as 'allow' | 'deny',
    };
  }

  private static parseOCISecurityRule(rule: any, direction: string): SecurityRuleContext {
    let portRange = 'all';

    if (rule.tcp_options?.destination_port_range) {
      const range = rule.tcp_options.destination_port_range;
      portRange = range.min === range.max ? String(range.min) : `${range.min}-${range.max}`;
    } else if (rule.udp_options?.destination_port_range) {
      const range = rule.udp_options.destination_port_range;
      portRange = range.min === range.max ? String(range.min) : `${range.min}-${range.max}`;
    }

    return {
      protocol: rule.protocol === 'all' ? 'all' : rule.protocol,
      portRange,
      source: direction === 'inbound' ? rule.source : rule.destination,
      description: rule.description,
    };
  }

  private static parseOCINSGRule(rule: any): SecurityRuleContext {
    let portRange = 'all';

    if (rule.tcp_options?.destination_port_range) {
      const range = rule.tcp_options.destination_port_range;
      portRange = range.min === range.max ? String(range.min) : `${range.min}-${range.max}`;
    }

    return {
      protocol: rule.protocol === 'all' ? 'all' : rule.protocol,
      portRange,
      source: rule.source || rule.destination,
      description: rule.description,
    };
  }

  private static findResourcesWithSG(sgId: string, vpc: any): string[] {
    const resources: string[] = [];

    // Find instances
    for (const instance of vpc.instances || []) {
      if (instance.security_groups?.includes(sgId)) {
        resources.push(instance.id);
      }
    }

    // Find ENIs
    for (const eni of vpc.network_interfaces || []) {
      if (eni.security_groups?.includes(sgId)) {
        resources.push(eni.id);
      }
    }

    return resources;
  }
}

